import { platformRequest } from '@/core/api/platformApi';

export type SmsProviderCode = 'sms-ir' | 'kavenegar';

export interface PlatformSmsProviderSettings {
  code: SmsProviderCode;
  provider: string;
  baseUrl: string;
  isEnabled: boolean;
  senderNumber: string | null;
  hasApiKey: boolean;
  isConfigured: boolean;
  updatedAtUtc: string | null;
  lastConnectionTestAtUtc: string | null;
  lastConnectionTestSucceeded: boolean | null;
  lastConnectionTestMessage: string | null;
  lastKnownCredit: number | null;
  lastKnownLineCount: number;
  supportsLineDiscovery: boolean;
  senderNumberRequired: boolean;
  creditUnit: string;
}

export interface PlatformSmsProvidersSettings {
  defaultProviderCode: SmsProviderCode;
  fallbackEnabled: boolean;
  fallbackProviderCode: SmsProviderCode | null;
  isConfigured: boolean;
  providers: PlatformSmsProviderSettings[];
}

export type LegacyPlatformSmsProviderSettings = Omit<
  PlatformSmsProviderSettings,
  'code' | 'supportsLineDiscovery' | 'senderNumberRequired' | 'creditUnit'
>;

export interface PlatformNotificationDashboard {
  provider: LegacyPlatformSmsProviderSettings;
  totalMessages: number;
  queuedMessages: number;
  sentToday: number;
  failedToday: number;
  requiresConfigurationToday: number;
}

export interface SmsProviderUpdateRequest {
  isEnabled: boolean;
  senderNumber: string | null;
  apiKey: string | null;
  clearApiKey: boolean;
}

export interface SmsConnectionTest {
  providerCode: SmsProviderCode;
  success: boolean;
  message: string;
  credit: number | null;
  lineCount: number;
}

export interface SmsSenderLine {
  lineNumber: string;
  isSelected: boolean;
}

export interface PlatformMessageTemplateVariable {
  key: string;
  label: string;
}

export interface PlatformMessageTemplate {
  eventCode: string;
  name: string;
  channel: string;
  isEnabled: boolean;
  message: string;
  updatedAtUtc: string | null;
  variables: PlatformMessageTemplateVariable[];
}

export interface UpdatePlatformMessageTemplateRequest {
  isEnabled: boolean;
  message: string;
}

export type PlatformDeliveryStatus = 'pending' | 'sent' | 'failed' | 'skipped' | 'requiresConfiguration';

export interface PlatformNotificationDelivery {
  id: string;
  tenantId: string;
  leadId: string;
  recipient: string | null;
  status: PlatformDeliveryStatus;
  subject: string;
  message: string;
  error: string | null;
  providerCode: SmsProviderCode | null;
  externalMessageId: string | null;
  attemptCount: number;
  createdAtUtc: string;
  completedAtUtc: string | null;
}

export const platformNotificationsApi = {
  dashboard: (): Promise<PlatformNotificationDashboard> =>
    platformRequest('/api/v1/platform/notifications/dashboard'),
  provider: (): Promise<LegacyPlatformSmsProviderSettings> =>
    platformRequest('/api/v1/platform/notifications/provider'),
  providers: (): Promise<PlatformSmsProvidersSettings> =>
    platformRequest('/api/v1/platform/notifications/providers'),
  updateRouting: (payload: {
    defaultProviderCode: SmsProviderCode;
    fallbackEnabled: boolean;
    fallbackProviderCode: SmsProviderCode | null;
  }): Promise<PlatformSmsProvidersSettings> =>
    platformRequest('/api/v1/platform/notifications/providers/routing', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  updateProvider: (
    providerCode: SmsProviderCode,
    payload: SmsProviderUpdateRequest,
  ): Promise<PlatformSmsProviderSettings> =>
    platformRequest(`/api/v1/platform/notifications/providers/${encodeURIComponent(providerCode)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  testProvider: (providerCode: SmsProviderCode): Promise<SmsConnectionTest> =>
    platformRequest(`/api/v1/platform/notifications/providers/${encodeURIComponent(providerCode)}/test`, { method: 'POST' }),
  lines: (providerCode: SmsProviderCode): Promise<SmsSenderLine[]> =>
    platformRequest(`/api/v1/platform/notifications/providers/${encodeURIComponent(providerCode)}/lines`),
  templates: (): Promise<PlatformMessageTemplate[]> =>
    platformRequest('/api/v1/platform/notifications/templates'),
  updateTemplate: (
    eventCode: string,
    payload: UpdatePlatformMessageTemplateRequest,
  ): Promise<PlatformMessageTemplate> =>
    platformRequest(`/api/v1/platform/notifications/templates/${encodeURIComponent(eventCode)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  deliveries: (filters?: { tenantId?: string; status?: string; take?: number }): Promise<PlatformNotificationDelivery[]> => {
    const params = new URLSearchParams();
    if (filters?.tenantId) params.set('tenantId', filters.tenantId);
    if (filters?.status) params.set('status', filters.status);
    params.set('take', String(filters?.take ?? 100));
    return platformRequest(`/api/v1/platform/notifications/deliveries?${params.toString()}`);
  },
  sendTest: (payload: { tenantId: string; mobile: string; message: string }): Promise<PlatformNotificationDelivery> =>
    platformRequest('/api/v1/platform/notifications/send-test', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  retry: (deliveryId: string): Promise<PlatformNotificationDelivery> =>
    platformRequest(`/api/v1/platform/notifications/deliveries/${deliveryId}/retry`, { method: 'POST' }),
};
